import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
    Avatar,
    Box,
    Card, CardContent,
    Container,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useStoreState } from 'pullstate';
import { useCallback, useEffect, useRef, useState } from 'react';
import BackdropLoader from '../../components/BackdropLoader.jsx';
import { AppStore } from '../../stores/AppStore.js';
import { getPeopleBuilderStatus } from '../../utils/getPeopleBuilderStatus.js';
import BreadcrumbsPeopleBuilder from './BreadcrumbsPeopleBuilder.jsx';
import DepartmentPeopleBuilderRow from './DepartmentPeopleBuilderRow.jsx';
import PeopleBuilderFilter from './PeopleBuilderFilter.jsx';

const DepartmentPeopleBuilder = () => {
    const rowRefs = useRef({});
    const cardRef = useRef(null);
    const [pbStatus, setPbStatus] = useState('')
    const [filteredPeopleBuilder, setFilteredPeopleBuilder] = useState([]);
    const [openFilter, setOpenFilter] = useState(false)
    const { peopleBuilders, loading, checkIns, user, strengths, goals } = useStoreState(AppStore)
    const userLogged = user
    const [currentUser, setCurrentUser] = useState(userLogged)
    const [breadcrumbsPb, setBreadcrumbsPb] = useState([userLogged?.department?.department, userLogged.full_name])
    const [statusToFilter, setStatusToFilter] = useState([])
    const [allSubPlayers, setAllSubPlayers] = useState([])
    const [playerNameToFilter, setPlayerNameToFilter] = useState('')
    const [yearToFilter, setYearToFilter] = useState('')
    const [functionSetPbAsTeam, setFunctionSetPbAsTeam] = useState(() => () => { });
    const [functionSetPbAsDepartment, setFunctionSetPbAsDepartment] = useState(() => () => { });

    const getAllSubPlayers = useCallback((player, allPlayers) => {
        let result = [];
        const directSubPlayers = allPlayers.filter(p =>
            p.player.coach === player?.player?.full_name &&
            (p.pb_settings.status === 'active' || p.pb_settings.status === 'previous')
        );

        for (const sub of directSubPlayers) {
            result.push(sub);
            result = result.concat(getAllSubPlayers(sub, allPlayers));
        }

        // Remove duplicate based on player_id + year to avoid repeated users
        const uniqueResult = result.filter((item, index, self) =>
            index === self.findIndex(t => t.player_id === item.player_id && t.year === item.year)
        );

        return uniqueResult;
    }, []);

    const rebuildAllSubPlayers = useCallback(() => {
        if (!peopleBuilders?.length) return;

        // Bring only the active people builder of the current user as a reference for the structure
        const referencePB = peopleBuilders.find(user =>
            user?.player?.full_name === currentUser?.full_name &&
            user.pb_settings.status === 'active'
        );

        if (referencePB) {
            // Get all subordinates (including all years)
            const allSubPlayers = getAllSubPlayers(referencePB, peopleBuilders);
            setAllSubPlayers(allSubPlayers);
        } else {
            // If there is no active PB, use a previous PB as a reference
            const fallbackPB = peopleBuilders.find(user =>
                user?.player?.full_name === currentUser?.full_name &&
                user.pb_settings.status === 'previous'
            );
            if (fallbackPB) {
                const allSubPlayers = getAllSubPlayers(fallbackPB, peopleBuilders);
                setAllSubPlayers(allSubPlayers);
            }
        }
    }, [peopleBuilders, currentUser, getAllSubPlayers]);

    const setpbAsTeam = useCallback(() => {
        if (!allSubPlayers?.length) {
            return [];
        }

        const filtered = allSubPlayers.filter(user => {
            let goalsStatus = goals.filter(goal => {
                return goal.people_builder.player_id === user.player_id &&
                    goal.people_builder.year === user.year
            });
            let pbstatus = getPeopleBuilderStatus(goalsStatus);

            // Year filter: just include if the year matches exactly (if yearToFilter is defined)
            let byYear = !yearToFilter || user.year.toString() === yearToFilter;
            let byStatus = statusToFilter.includes(pbstatus.status) || !statusToFilter?.length;
            let byCoach = user.player.coach === currentUser?.full_name;

            return byYear && byStatus && byCoach;
        });

        return filtered;
    }, [allSubPlayers, goals, statusToFilter, currentUser, yearToFilter])

    const setpbAsDepartment = useCallback(() => {
        if (!allSubPlayers?.length) {
            return [];
        }

        const filtered = allSubPlayers.filter(user => {
            let goalsStatus = goals.filter(goal => {
                return goal.people_builder.player_id === user.player_id &&
                    goal.people_builder.year === user.year
            });
            let pbstatus = getPeopleBuilderStatus(goalsStatus);

            // Year filter: just include if the year matches exactly (if yearToFilter is defined)
            let byYear = !yearToFilter || user.year.toString() === yearToFilter;
            let byStatus = statusToFilter.includes(pbstatus.status) || !statusToFilter?.length;

            return byYear && byStatus;
        });

        return filtered;
    }, [allSubPlayers, goals, statusToFilter, yearToFilter])

    useEffect(() => {
        if (!openFilter && peopleBuilders?.length) {
          setStatusToFilter([]);
          setPlayerNameToFilter('');
      
          const active = peopleBuilders.find(
            pb => pb.pb_settings.status === 'active'
          );
      
          if (active) {
            setYearToFilter(active.year.toString());
          } else {
            const previousYears = peopleBuilders
              .filter(pb => pb.pb_settings.status === 'previous')
              .map(pb => pb.year);
      
            setYearToFilter(
              previousYears?.length
                ? Math.max(...previousYears).toString()
                : ''
            );
          }
        }
        console.log()
      }, [openFilter, peopleBuilders]);
      

    // Initialize allSubPlayers when peopleBuilders and currentUser are available
    useEffect(() => {
            rebuildAllSubPlayers();
        
    }, [rebuildAllSubPlayers]);

    useEffect(() => {
        if (!allSubPlayers?.length || !goals?.length) return;
    
        let filtered = openFilter
            ? setpbAsDepartment()
            : setpbAsTeam();
    
        if (openFilter && playerNameToFilter) {
            filtered = filtered.filter(user =>
                user.player.full_name
                    .toLowerCase()
                    .includes(playerNameToFilter.toLowerCase())
            );
        }
    
        const uniqueFiltered = filtered.filter(
            (item, index, self) =>
                index === self.findIndex(
                    t => t.player_id === item.player_id && t.year === item.year
                )
        );
    
        uniqueFiltered.sort((a, b) =>
            a.player.full_name.localeCompare(b.player.full_name)
        );

    
        setFilteredPeopleBuilder(uniqueFiltered);
    }, [
        allSubPlayers,
        goals,
        openFilter,
        statusToFilter,
        yearToFilter,
        playerNameToFilter,
        setpbAsTeam,
        setpbAsDepartment
    ]);
    

    // Rebuild allSubPlayers when yearToFilter changes to ensure all year data is available
    useEffect(() => {
        if (yearToFilter) {
            rebuildAllSubPlayers();
        }
    }, [yearToFilter, rebuildAllSubPlayers]);

    useEffect(() => {
        if (filteredPeopleBuilder?.length) {
            AppStore.update((s) => { s.loading = false })
        }
    }, [filteredPeopleBuilder])

    const handleScroll = (playerId) => {
        const rowElement = rowRefs.current[playerId];
        if (rowElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const cardElement = cardRef.current;
            // cardElement ? cardElement.scrollBy({ top: - 70, behavior: 'smooth' }) : '';
        }
    };

    return (
        <Container maxWidth='xl' sx={{ mt: 10 }} >
            <BackdropLoader openBackdrop={loading} />
            <Card ref={cardRef} sx={{ mt: 4, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: 'auto', p: 2, overflow: 'hidden' }}>
                    <Box sx={{ flexShrink: 0 }}>
                        <Typography variant='h5' align='center'>Department People Builder</Typography>
                        <Box sx={{ minHeight: 40, mb: 2, mt: 2, display: 'flex', alignItems: 'center', ml: 2 }}>
                            <BreadcrumbsPeopleBuilder
                                breadcrumbsPb={breadcrumbsPb}
                                setBreadcrumbsPb={setBreadcrumbsPb}
                                setCurrentUser={setCurrentUser}
                                currentUser={currentUser}
                                playerNameToFilter={playerNameToFilter}
                                setPlayerNameToFilter={setPlayerNameToFilter}
                                openFilter={openFilter}
                                setOpenFilter={setOpenFilter}
                                />
                        </Box>
                                {openFilter && (
                                    <PeopleBuilderFilter
                                        component='department'
                                        openFilter={openFilter}
                                        setOpenFilter={setOpenFilter}
                                        setFilteredPeopleBuilder={setFilteredPeopleBuilder}
                                        filteredPeopleBuilder={filteredPeopleBuilder}
                                        currentUser={currentUser}
                                        setCurrentUser={setCurrentUser}
                                        setStatusToFilter={setStatusToFilter}
                                        statusToFilter={statusToFilter}
                                        allSubPlayers={allSubPlayers}
                                        setBreadcrumbsPb={setBreadcrumbsPb}
                                        breadcrumbsPb={breadcrumbsPb}
                                        playerNameToFilter={playerNameToFilter}
                                        setPlayerNameToFilter={setPlayerNameToFilter}
                                        yearToFilter={yearToFilter}
                                        setYearToFilter={setYearToFilter}
                                        functionSetPbAsTeam={functionSetPbAsTeam}
                                        functionSetPbAsDepartment={functionSetPbAsDepartment}
                                    />
                                )}
                    </Box>
                    <TableContainer sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 }} >
                        <Table stickyHeader aria-label="collapsible table" >
                            <TableHead>
                                <TableRow>
                                    <TableCell colSpan={3} align='center' sx={{ backgroundColor: 'lightgray' }}>
                                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ width: '100%', gap:1 }}>
                                                <Box sx={{display: 'flex',        flexDirection: 'row',   alignItems: 'center',  }}>
    
                                                <IconButton
                                    
                                                    onClick={() => {
                                                        setOpenFilter(!openFilter)
                                                    }}>
                                                    <FilterAltIcon />
                                                </IconButton>
                                                <Avatar src={currentUser?.photo_url} />
                                                <Typography fontWeight='bold' ml={2} color="initial" align='left'>{currentUser?.full_name}</Typography>
                                                </Box>
                                                <Stack direction='row' justifyContent='space-between' sx={{ width: '70%' }}>
                                                    <Typography fontWeight='bold' color="initial" align='left'>
                                                        {currentUser?.title?.title?.toUpperCase()}
                                                    </Typography>
                                                    <Typography fontWeight='bold' color="initial" align='left'>
                                                        {currentUser?.department?.department?.toUpperCase()}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                </TableRow>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography sx={{ fontSize: '16px', color: 'darkgray', fontStyle: 'italic' }} variant="h5" color="initial" align='center'>Loading...</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredPeopleBuilder?.length ? (
                                        filteredPeopleBuilder?.map((user) => (
                                            <DepartmentPeopleBuilderRow
                                                key={user.id}
                                                ref={(el) => (rowRefs.current[user.player.id + user.year] = el)}
                                                pbStatus={pbStatus}
                                                setBreadcrumbsPb={setBreadcrumbsPb}
                                                setFilteredPeopleBuilder={setFilteredPeopleBuilder}
                                                filteredPeopleBuilder={filteredPeopleBuilder}
                                                setCurrentUser={setCurrentUser}
                                                setPbStatus={setPbStatus}
                                                playerId={user.player_id}
                                                peopleBuilder={user}
                                                checkIns={checkIns}
                                                handleScroll={() => handleScroll(user.player.id + user.year)}
                                                strengths={strengths}
                                            />
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="initial" align='center'>No Players Found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Container>
    )
}

export default DepartmentPeopleBuilder